/**
 * Run an async operation, retrying a few times on failure with a short backoff.
 *
 * Used to make multi-request save flows resilient to a single transient
 * network/backend hiccup. When several writes fire together (e.g. the campaign
 * create/edit flow PUTs base config + flow + evaluation + automation in
 * parallel), one flaky request would otherwise reject the whole batch and force
 * the user to redo the step. Retrying that single call recovers silently.
 *
 * @param fn       the async operation to run
 * @param attempts total attempts including the first (default 3 = 1 try + 2 retries)
 * @param delayMs  delay between attempts (default 500ms)
 */
export async function withRetry<T>(
    fn: () => Promise<T>,
    attempts = 3,
    delayMs = 500
): Promise<T> {
    let lastError: unknown;
    for (let i = 0; i < attempts; i++) {
        try {
            return await fn();
        } catch (err) {
            lastError = err;
            if (i < attempts - 1) {
                await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
        }
    }
    throw lastError;
}
